import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useTaskCreate,
  useTaskUpdate,
  extractErrorMessage,
  useGetUser,
  PriorityLevel,
  useTaskOne,
  TaskCategory,
  TaskStatus,
  TagType,
  getBackendTimestamp,
  useGetOrganizationById,
  useGetOneTicket,
  useInternalPortalVendorGetById,
  TaskDto,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { useValidationSchema, ValidationRule } from '@sib/ui/hooks';
import {
  FormMode,
  getTicketNumberLabel,
  useOrganizationsAutocompleteProps,
  useTicketsAutocompleteProps,
  useVendorsAutocompleteProps,
} from '@sib/utils';
import { getTagIdsByType, mapFormTagsToTaskDto } from '@sib/utils/tags';
import { TaskField } from '@sib/utils/tasks';
import { z } from 'zod';

export type TaskFormOutputValues = z.output<
  ReturnType<typeof useTaskFormValidationSchema>
>;

type TaskFormInputValues = z.input<
  ReturnType<typeof useTaskFormValidationSchema>
>;

export const useTaskFormValidationSchema = () => {
  const { rules } = useValidationSchema();

  return useMemo(
    () =>
      z.object({
        [TaskField.Title]: rules[ValidationRule.TaskTitle],
        [TaskField.Description]: rules[ValidationRule.TaskDescription],
        [TaskField.PriorityLevel]: rules[ValidationRule.PriorityLevel],
        [TaskField.Category]: rules[ValidationRule.TaskCategory],
        [TaskField.Status]: rules[ValidationRule.TaskStatus],
        [TaskField.IsCompleted]: z.boolean().optional(),
        [TaskField.Tag]: rules[ValidationRule.TaskTag],
        [TaskField.ReferenceLink]: rules[ValidationRule.ReferenceLink],
        [TaskField.ReferenceName]: rules[ValidationRule.ReferenceName],
        [TaskField.Deadline]: rules[ValidationRule.ProperDate],
        [TaskField.Organization]: z.string().optional().nullable(),
        [TaskField.Vendor]: rules[ValidationRule.TicketVendor],
        [TaskField.Ticket]: z.string().optional().nullable(),
        [TaskField.Assignee]: rules[ValidationRule.TaskAssignee],
        [TaskField.CreatedBy]: z.string().optional(),
        [TaskField.EstimatedTime]: rules[ValidationRule.TaskEstimatedTime],
        [TaskField.IsNextStep]: z.boolean().optional(),
      }),
    [rules]
  );
};

export const useTaskFormDefaultValues = (
  taskId?: string | null,
  taskContext?: TaskFormEntityContext
): TaskFormOutputValues => {
  const { data: userData } = useGetUser();
  const { data: task } = useTaskOne(taskId ?? '');

  const tagIdsByType = useMemo(
    () => getTagIdsByType(task?.tags || []),
    [task?.tags]
  );

  return useMemo(
    () => ({
      [TaskField.Title]: task?.title ?? '',
      [TaskField.Description]: task?.description ?? '',
      [TaskField.PriorityLevel]: task?.priorityLevel ?? PriorityLevel.Medium,
      [TaskField.Category]: task?.category || TaskCategory.VendorCall,
      [TaskField.Status]: task?.status ?? TaskStatus.New,
      [TaskField.IsCompleted]: task?.completed ?? false,
      [TaskField.ReferenceLink]: task?.referenceLink ?? '',
      [TaskField.ReferenceName]: task?.referenceName ?? '',
      [TaskField.Deadline]: task?.deadline
        ? new Date(task?.deadline)
        : new Date(),
      [TaskField.Organization]:
        task?.organization?.id ?? taskContext?.organizationId ?? null,
      [TaskField.Vendor]: task?.vendor?.id ?? taskContext?.vendorId ?? null,
      [TaskField.Ticket]: task?.ticketId ?? taskContext?.ticketId ?? null,
      [TaskField.Assignee]: task?.assigneeUser?.id || userData?.id || '',
      [TaskField.CreatedBy]: task?.createdByUser?.id || '',
      [TaskField.EstimatedTime]: task?.estimatedTime ?? 0,
      [TaskField.Tag]: {
        [TagType.Custom]: tagIdsByType[TagType.Custom],
        [TagType.Contact]: tagIdsByType[TagType.Contact],
        [TagType.Employee]: tagIdsByType[TagType.Employee],
      },
      [TaskField.IsNextStep]: !!task?.parentTask,
    }),
    [task, taskContext, userData, tagIdsByType]
  );
};

export interface TaskFormEntityContext {
  projectId?: string;
  vendorId?: string;
  organizationId?: string;
  ticketId?: string;
}

export interface UseTaskFormOptions {
  taskId?: string;
  type: FormMode;
  onClose?: () => void;
  onCancel?: () => void;
  taskContext?: TaskFormEntityContext;
  /** Creates the task as a next step of the given task. */
  nextStepParentTaskId?: string | null;
}

interface UseTaskFieldsOptionsParams {
  taskId?: string | null;
  defaultOrganizationId?: string;
  defaultVendorId?: string;
  defaultTicketId?: string;
}

export const useTaskFieldsOptions = ({
  taskId,
  defaultOrganizationId,
  defaultVendorId,
  defaultTicketId,
}: UseTaskFieldsOptionsParams) => {
  const { data: task } = useTaskOne(taskId ?? '');

  const { data: organizationData } = useGetOrganizationById(
    task?.organization?.id ?? defaultOrganizationId ?? ''
  );

  const { data: defaultVendorData } = useInternalPortalVendorGetById(
    defaultVendorId ?? ''
  );

  const organizationsAutocompleteProps = useOrganizationsAutocompleteProps({
    currentOrgData: organizationData,
  });

  const { data: relatedTicket } = useGetOneTicket(
    task?.ticketId ?? defaultTicketId ?? ''
  );
  const ticketAdditionalFields = useMemo(() => {
    const label = relatedTicket ? getTicketNumberLabel(relatedTicket) : null;

    if (!relatedTicket || !label) return [];

    return [{ label, value: relatedTicket.id }];
  }, [relatedTicket]);

  const ticketsAutocompleteProps = useTicketsAutocompleteProps({
    additionalFields: ticketAdditionalFields,
  });

  const vendorFromTask = task?.vendor;
  const vendorAdditionalFields = useMemo(() => {
    if (vendorFromTask)
      return [{ label: vendorFromTask.name, value: vendorFromTask.id }];

    if (defaultVendorData)
      return [{ label: defaultVendorData.name, value: defaultVendorData.id }];

    return [];
  }, [vendorFromTask, defaultVendorData]);

  const vendorsAutocompleteProps = useVendorsAutocompleteProps({
    additionalFields: vendorAdditionalFields,
  });

  return {
    vendorsAutocompleteProps,
    ticketsAutocompleteProps,
    organizationsAutocompleteProps: {
      ...organizationsAutocompleteProps,
      disabled: false,
      readOnly: !!organizationData,
    },
  };
};

export const useTaskForm = ({
  taskId,
  type,
  onClose,
  onCancel,
  taskContext,
  nextStepParentTaskId,
}: UseTaskFormOptions) => {
  const { t } = useTranslation();

  const { open } = useNotification();

  const [nextStepParentTask, setNextStepParentTask] = useState<TaskDto | null>(
    null
  );
  const isNextStepSubmitRef = useRef(false);

  const parentTaskId = nextStepParentTask?.uuid ?? nextStepParentTaskId ?? null;
  const isCreatingNextStep = !!parentTaskId;
  const currentTaskId = isCreatingNextStep ? null : taskId;

  const isEdit = !!currentTaskId && type === FormMode.Edit;
  const { data: task, isLoading: isLoadingTask } = useTaskOne(
    currentTaskId ?? ''
  );

  const validationSchema = useTaskFormValidationSchema();
  const taskDefaultValues = useTaskFormDefaultValues(
    currentTaskId,
    taskContext
  );
  const defaultValues = useMemo(
    () => ({
      ...taskDefaultValues,
      [TaskField.IsNextStep]:
        taskDefaultValues[TaskField.IsNextStep] || isCreatingNextStep,
    }),
    [taskDefaultValues, isCreatingNextStep]
  );

  const formProps = useForm<TaskFormInputValues, null, TaskFormOutputValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const taskFormCallbacks = useMemo(() => {
    const operation =
      type === FormMode.Create || isCreatingNextStep ? 'create' : 'update';
    const successMessage = t(`notifications.${operation}.success`, {
      entity: t('entities.task'),
    });
    const errorMessage = t(`notifications.${operation}.failure`, {
      entity: t('entities.task'),
    });

    return {
      onSuccess: (data: TaskDto) => {
        open({
          type: 'success',
          message: successMessage,
        });

        if (isNextStepSubmitRef.current) {
          isNextStepSubmitRef.current = false;

          return setNextStepParentTask(data);
        }

        onClose?.();
      },
      onError: (error: Error) => {
        isNextStepSubmitRef.current = false;
        onClose?.();
        const apiErrorMessage = extractErrorMessage(error);

        open({
          type: 'error',
          message: apiErrorMessage || errorMessage,
        });
      },
    };
  }, [open, type, t, onClose, isCreatingNextStep]);

  const { mutateAsync: editTask, isPending: isTaskEditing } =
    useTaskUpdate(taskFormCallbacks);

  const { mutateAsync: createTask, isPending: isTaskCreating } =
    useTaskCreate(taskFormCallbacks);

  const onSubmit = useCallback(
    async (values: TaskFormOutputValues) => {
      const {
        [TaskField.CreatedBy]: _,
        [TaskField.Assignee]: assignee,
        [TaskField.Deadline]: deadline,
        [TaskField.Tag]: tags,
        [TaskField.Organization]: organizationId,
        [TaskField.Vendor]: vendorId,
        [TaskField.IsCompleted]: completed,
        [TaskField.IsNextStep]: _isNextStep,
        [TaskField.Status]: status,
        ...restValues
      } = values;
      const mappedTags = mapFormTagsToTaskDto(tags);

      const isAssigneeSoftDeleted =
        task?.assigneeUser?.id === assignee && !!task?.assigneeUser?.deletedAt;

      const resolvedOrganizationId =
        organizationId ??
        task?.organization?.id ??
        taskContext?.organizationId ??
        null;
      const resolvedVendorId =
        vendorId ?? task?.vendor?.id ?? taskContext?.vendorId ?? null;

      const formValues = {
        ...restValues,
        ...(!isAssigneeSoftDeleted && { assignee }),
        organizationId: resolvedOrganizationId,
        vendorId: resolvedVendorId,
      };

      const wasCompleted = !!task?.completed;
      const hasCompletionToggled = !!completed !== wasCompleted;
      const isCompleted = hasCompletionToggled
        ? !!completed
        : status === TaskStatus.Completed;
      const reopenedStatus =
        status === TaskStatus.Completed ? TaskStatus.InProgress : status;

      const completionPayload = {
        completed: isCompleted,
        status: isCompleted ? TaskStatus.Completed : reopenedStatus,
      };

      if (isEdit)
        return await editTask({
          ...formValues,
          ...completionPayload,
          tags: mappedTags,
          uuid: currentTaskId,
        });

      return await createTask({
        ...formValues,
        ...completionPayload,
        ...(taskContext?.projectId && { projectId: taskContext.projectId }),
        ...(parentTaskId && { followUpParentTaskId: parentTaskId }),
        tags: mappedTags,
        deadline: getBackendTimestamp(deadline),
      });
    },
    [
      editTask,
      createTask,
      task,
      isEdit,
      currentTaskId,
      taskContext,
      parentTaskId,
    ]
  );

  const onSubmitHandler = useCallback(
    async (values: TaskFormOutputValues) => {
      formProps.reset(values);

      return await onSubmit(values);
    },
    [formProps, onSubmit]
  );

  const onCreateNextStep = useMemo(
    () =>
      formProps.handleSubmit(async values => {
        isNextStepSubmitRef.current = true;

        return await onSubmitHandler({
          ...values,
          [TaskField.IsCompleted]: true,
        });
      }),
    [formProps, onSubmitHandler]
  );

  const onCancelHandler = useCallback(() => {
    formProps.reset();
    (onCancel ?? onClose)?.();
  }, [formProps, onCancel, onClose]);

  useEffect(() => formProps.reset(defaultValues), [formProps, defaultValues]);

  return {
    formProps,
    isEdit,
    isLoadingTask,
    currentTaskId,
    nextStepParentTask,
    isCreatingNextStep,
    onSubmit: onSubmitHandler,
    onCreateNextStep,
    onCancel: onCancelHandler,
    isSubmitting: isTaskCreating || isTaskEditing,
  };
};
